import { toPng } from 'html-to-image'

// Snapshot of the open activity's task area (registered by InteractiveActivityRenderer), taken at send time.
export const SNAPSHOT_MAX_CHARS = 600_000 // backend cap for instance.imageData
export const SNAPSHOT_TIMEOUT_MS = 1500 // per rasterize attempt
export const SNAPSHOT_TOTAL_TIMEOUT_MS = 3000 // whole capture, warm-up and retry included

let target: HTMLElement | null = null

export const registerSnapshotTarget = (el: HTMLElement | null): void => {
  target = el
}
export const unregisterSnapshotTarget = (el: HTMLElement): void => {
  if (target === el) target = null
}

/** WebKit skips embedding images on html-to-image's first pass (bubkoo/html-to-image #361, #420).
 *  Chrome/Edge UAs also contain "AppleWebKit", hence the exclusion. */
export const isWebKit = (): boolean =>
  typeof navigator !== 'undefined' &&
  /AppleWebKit/.test(navigator.userAgent) &&
  !/Chrome|Chromium|Edg/.test(navigator.userAgent)

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T | null> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(null)
      },
    )
  })

const excludeMarked = (node: HTMLElement): boolean => !node.classList?.contains('exclude-from-screenshot')

/** skipFonts: embedding web fonts made captures ~14 MB and miss the timeout; fallback fonts are legible enough.
 *  cacheBust: the CDN images are cached without CORS headers. */
const rasterize = (el: HTMLElement, pixelRatio: number): Promise<string | null> =>
  withTimeout(
    toPng(el, { backgroundColor: '#fff', pixelRatio, cacheBust: true, skipFonts: true, filter: excludeMarked }),
    SNAPSHOT_TIMEOUT_MS,
  )

const doCapture = async (el: HTMLElement): Promise<string | null> => {
  // Discarded warm-up pass so the real pass embeds images.
  if (isWebKit()) await rasterize(el, 1)
  const full = await rasterize(el, 1)
  if (!full) return null
  if (full.length <= SNAPSHOT_MAX_CHARS) return full
  const half = await rasterize(el, 0.5)
  return half && half.length <= SNAPSHOT_MAX_CHARS ? half : null
}

/** PNG data URL of the registered task area, or null. Never throws. */
export async function captureTaskSnapshot(): Promise<string | null> {
  if (!target || typeof window === 'undefined') return null
  const el = target
  try {
    return await withTimeout(doCapture(el), SNAPSHOT_TOTAL_TIMEOUT_MS)
  } catch {
    return null
  }
}
